import Stack from '@mui/material/Stack';
import {DataGrid, type GridColDef, type GridRowParams, type GridSortModel} from '@mui/x-data-grid';
import {enUS, esES} from '@mui/x-data-grid/locales';
import type {FC} from 'react';
import {useTranslation} from 'react-i18next';
import StatusInfo from '@/components/common/StatusInfo';
import type {ForestMassStatus} from '@/domain/entities/ForestMassStatus';
import {createStatusSortComparator} from './statusSortComparator';

//TYPES
type Props = {
  forestMassesStatuses?: Array<ForestMassStatus>;
  onForestMassClick: (uuid: string) => void;
  sortModel?: GridSortModel;
  onSortModelChange?: (model: GridSortModel) => void;
};

const ForestMassesTable: FC<Props> = ({forestMassesStatuses, onForestMassClick, sortModel, onSortModelChange}) => {
  const {t, i18n} = useTranslation();
  const lang = i18n.language;

  const baseLocale =
    lang === 'es'
      ? esES.components.MuiDataGrid.defaultProps.localeText
      : enUS.components.MuiDataGrid.defaultProps.localeText;

  const columns = [
    createColumn('createdAt', t('forestMass.date').toUpperCase(), {flex: 0.75}),
    createColumn('code', t('forestMass.code').toUpperCase(), {flex: 0.5}),
    createColumn('concello', t('forestMass.concello').toUpperCase(), {flex: 0.5}),
    createColumn('appraiserName', t('forestMass.appraiser').toUpperCase(), {flex: 0.75}),
    createColumn('plannedCuttingDate', t('forestMass.plannedCuttingDate').toUpperCase()),
    createColumn('finsaStatus', 'FINSA', {
      sortComparator: createStatusSortComparator('finsa', t, lang),
      renderCell: (params) => (
        <Stack my={2}>
          <StatusInfo id={params.row.finsaStatus} date={params.row.finsaStatusChangeDate} />
        </Stack>
      ),
    }),
    createColumn('aseforStatus', 'ASEFOR', {
      sortComparator: createStatusSortComparator('asefor', t, lang),
      renderCell: (params) => (
        <Stack my={2}>
          <StatusInfo id={params.row.aseforStatus} date={params.row.aseforStatusChangeDate} />
        </Stack>
      ),
    }),
    createColumn('fortopStatus', 'FORTOP', {
      sortComparator: createStatusSortComparator('fortop', t, lang),
      renderCell: (params) => (
        <Stack my={2}>
          <StatusInfo id={params.row.fortopStatus} date={params.row.fortopStatusChangeDate} />
        </Stack>
      ),
    }),
  ];

  const forestMassesForTable = forestMassesStatuses?.map((mass) => ({
    id: mass.forestMassUuid,
    code: mass.code,
    createdAt: mass.createdAt.format('YYYY-MM-DD'),
    concello: mass.concello,
    appraiserName: mass.appraiserName,
    plannedCuttingDate: mass.plannedCuttingDate.format('YYYY-MM-DD'),
    finsaStatus: mass.finsaStatus,
    finsaStatusChangeDate: mass.finsaStatusChangeDate,
    aseforStatus: mass.aseforStatus,
    aseforStatusChangeDate: mass.aseforStatusChangeDate,
    fortopStatus: mass.fortopStatus,
    fortopStatusChangeDate: mass.fortopStatusChangeDate,
  }));

  const handleForestMassClick = (params: GridRowParams) => {
    onForestMassClick(params.id as string);
  };

  return (
    <DataGrid
      rows={forestMassesForTable}
      columns={columns}
      disableColumnSelector
      disableColumnMenu
      disableColumnFilter
      getRowHeight={() => 'auto'}
      onRowClick={handleForestMassClick}
      sortModel={sortModel}
      onSortModelChange={onSortModelChange}
      localeText={{
        ...baseLocale,
        noRowsLabel: t('forestMass.empty'),
      }}
      sx={styles.table}
    />
  );
};
export default ForestMassesTable;

const createColumn = (field: string, label: string, extraProps?: Partial<GridColDef>) => ({
  field: field,
  headerName: label,
  flex: extraProps?.flex !== undefined ? extraProps?.flex : 1,
  ...extraProps,
});

const styles = {
  table: {
    width: '100%',
    boxShadow: 'none',
    borderRadius: '0px',
    '& .MuiDataGrid-cell': {
      display: 'flex',
      alignItems: 'center',
    },
    '& .MuiPaper-root': {
      boxShadow: 'none !important',
    },
    '--Paper-shadow': 'none',
    '& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeaderTitle': {
      color: 'common.white',
      fontWeight: 900,
      backgroundColor: 'primary.main',
    },
    '& .MuiDataGrid-cell:focus': {outline: 'none'},
    // Iconos de ordenar
    '& .MuiDataGrid-columnHeader--sortable .MuiSvgIcon-root': {
      color: 'common.white', // color de los iconos de ordenar
    },
    // Iconos de menú/filtro
    '& .MuiDataGrid-columnHeader:hover .MuiIconButton-root': {
      backgroundColor: 'primary.main',
      color: 'common.white',
    },
    // Icono de ordenar cuando está activo (sin hover)
    '& .MuiDataGrid-columnHeader--sorted .MuiButtonBase-root': {
      backgroundColor: 'primary.main',
    },
  },
};
